import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Upload PDF to Firebase Storage using Cloud Function (to avoid CORS issues)
 * @param {Blob} pdfBlob - PDF file blob
 * @param {string} documentType - 'quotation' or 'invoice'
 * @param {string} documentId - Document ID
 * @param {string} documentNumber - Document number
 * @param {number} version - Version number
 * @param {boolean} isHistoricalImport - True if from CSV import
 * @returns {Promise<Object>} Upload metadata
 */
export const uploadPDFToStorage = async (
  pdfBlob,
  documentType,
  documentId,
  documentNumber,
  version = 1,
  isHistoricalImport = false
) => {
  try {
    console.log('Starting PDF upload via Cloud Function...', {
      documentType,
      documentId,
      documentNumber,
      version
    });

    // Convert PDF blob to base64
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove the data URL prefix if present
        const base64 = reader.result.split(',')[1] || reader.result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });

    console.log('PDF converted to base64, size:', base64Data.length, 'bytes');

    // Call Cloud Function to upload PDF (avoids CORS issues)
    const functions = getFunctions();
    const uploadFunction = httpsCallable(functions, 'uploadAccountingPDF');
    
    const result = await uploadFunction({
      pdfData: base64Data,
      documentType,
      documentId,
      documentNumber,
      version
    });

    console.log('Cloud Function upload completed successfully:', result.data);

    // Return metadata from Cloud Function response
    return {
      ...result.data.metadata,
      isHistoricalImport
    };
  } catch (error) {
    console.error('Error uploading PDF to storage:', error);
    
    // Provide more detailed error message
    if (error.code) {
      throw new Error(`Failed to upload PDF (${error.code}): ${error.message}`);
    }
    throw new Error(`Failed to upload PDF: ${error.message}`);
  }
};

/**
 * Get download URL for stored PDF using Cloud Function (to avoid CORS issues)
 * @param {string} storagePath - Firebase Storage path
 * @returns {Promise<string>} Download URL
 */
export const downloadPDFFromStorage = async (storagePath) => {
  try {
    console.log('Getting download URL via Cloud Function for:', storagePath);

    // Call Cloud Function to get download URL (avoids CORS issues)
    const functions = getFunctions();
    const getURLFunction = httpsCallable(functions, 'getStoragePDFDownloadURL');
    
    const result = await getURLFunction({ storagePath });

    console.log('Cloud Function returned download URL successfully');
    
    return result.data.downloadURL;
  } catch (error) {
    console.error('Error getting download URL:', error);
    
    // Provide more detailed error message
    if (error.code) {
      throw new Error(`Failed to get download URL (${error.code}): ${error.message}`);
    }
    throw new Error(`Failed to get download URL: ${error.message}`);
  }
};

/**
 * Download PDF blob from storage using Cloud Function (to completely avoid CORS issues)
 * Returns the PDF content as a blob for direct download
 * @param {string} storagePath - Firebase Storage path
 * @returns {Promise<{blob: Blob, fileName: string}>} PDF blob and filename
 */
export const downloadPDFBlobFromStorage = async (storagePath) => {
  try {
    console.log('Downloading PDF blob via Cloud Function for:', storagePath);

    // Call Cloud Function to download PDF as base64 (bypasses CORS completely)
    const functions = getFunctions();
    const downloadFunction = httpsCallable(functions, 'downloadStoragePDFBlob');
    
    const result = await downloadFunction({ storagePath });

    console.log('Cloud Function returned PDF blob successfully');
    
    // Convert base64 back to blob
    const base64Data = result.data.pdfData;
    const binaryString = window.atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: result.data.contentType || 'application/pdf' });
    
    return {
      blob,
      fileName: result.data.fileName
    };
  } catch (error) {
    console.error('Error downloading PDF blob:', error);
    
    // Provide more detailed error message
    if (error.code) {
      throw new Error(`Failed to download PDF (${error.code}): ${error.message}`);
    }
    throw new Error(`Failed to download PDF: ${error.message}`);
  }
};

/**
 * Delete PDF from Firebase Storage using Cloud Function (to avoid CORS issues)
 * @param {string} storagePath - Firebase Storage path
 * @param {string} documentId - Document ID
 * @param {string} documentType - 'quotation' or 'invoice'
 * @param {number} version - Version to delete
 * @returns {Promise<boolean>} Success status
 */
export const deletePDFFromStorage = async (storagePath, documentId, documentType, version) => {
  try {
    console.log('Deleting PDF via Cloud Function:', { storagePath, documentId, documentType, version });

    // Call Cloud Function to delete PDF (avoids CORS issues)
    const functions = getFunctions();
    const deleteFunction = httpsCallable(functions, 'deleteStoragePDF');
    
    const result = await deleteFunction({
      storagePath,
      documentId,
      documentType,
      version
    });

    console.log('Cloud Function deletion completed successfully:', result.data);

    return true;
  } catch (error) {
    console.error('Error deleting PDF from storage:', error);
    
    // Provide more detailed error message
    if (error.code) {
      throw new Error(`Failed to delete PDF (${error.code}): ${error.message}`);
    }
    throw new Error(`Failed to delete PDF: ${error.message}`);
  }
};

/**
 * List all PDF versions for a document
 * @param {string} documentType - 'quotation' or 'invoice'
 * @param {string} documentId - Document ID
 * @returns {Promise<Array<Object>>} Array of version metadata
 */
export const listPDFVersions = async (documentType, documentId) => {
  try {
    const collectionName = documentType === 'quotation' ? 'quotations' : 'invoices';
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const versions = data.pdfArchive?.versions || [];
      
      // Sort by version number descending
      return versions.sort((a, b) => b.version - a.version);
    }

    return [];
  } catch (error) {
    console.error('Error listing PDF versions:', error);
    throw new Error(`Failed to list PDF versions: ${error.message}`);
  }
};

/**
 * Get PDF metadata for a document
 * @param {string} documentType - 'quotation' or 'invoice'
 * @param {string} documentId - Document ID
 * @returns {Promise<Object|null>} PDF metadata or null
 */
export const getPDFMetadata = async (documentType, documentId) => {
  try {
    const collectionName = documentType === 'quotation' ? 'quotations' : 'invoices';
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.pdfArchive?.metadata || null;
    }

    return null;
  } catch (error) {
    console.error('Error getting PDF metadata:', error);
    return null;
  }
};

/**
 * Get next version number for a document
 * @param {string} documentType - 'quotation' or 'invoice'
 * @param {string} documentId - Document ID
 * @returns {Promise<number>} Next version number
 */
export const getNextVersion = async (documentType, documentId) => {
  try {
    const versions = await listPDFVersions(documentType, documentId);
    if (versions.length === 0) {
      return 1;
    }
    return Math.max(...versions.map(v => v.version)) + 1;
  } catch (error) {
    console.error('Error getting next version:', error);
    return 1;
  }
};

/**
 * Check if document has stored PDF
 * @param {string} documentType - 'quotation' or 'invoice'
 * @param {string} documentId - Document ID
 * @returns {Promise<boolean>} True if PDF stored
 */
export const hasPDFStored = async (documentType, documentId) => {
  try {
    const metadata = await getPDFMetadata(documentType, documentId);
    return metadata !== null;
  } catch (error) {
    console.error('Error checking PDF storage:', error);
    return false;
  }
};

// ============================================================================
// NEW: Invoice Document Type-Specific Functions
// ============================================================================

/**
 * Upload Invoice Document by Type
 * Uploads a document (Invoice, DO, PO, RT) with standardized naming
 * @param {Blob} pdfBlob - PDF file blob
 * @param {string} invoiceId - Invoice document ID
 * @param {string} invoiceNumber - Invoice number (e.g., BSIN-122023-134)
 * @param {string} docTypeKey - Document type key ('invoice', 'deliveryOrder', 'purchaseOrder', 'rentalTerms')
 * @param {Object} additionalMetadata - Additional metadata (e.g., clientPONumber)
 * @returns {Promise<Object>} Upload metadata
 */
export const uploadInvoiceDocument = async (
  pdfBlob,
  invoiceId,
  invoiceNumber,
  docTypeKey,
  additionalMetadata = {}
) => {
  try {
    console.log('Starting invoice document upload...', {
      invoiceId,
      invoiceNumber,
      docTypeKey
    });

    // Convert PDF blob to base64
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1] || reader.result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });

    console.log('PDF converted to base64, size:', base64Data.length, 'bytes');

    // Call Cloud Function with document type
    const functions = getFunctions();
    const uploadFunction = httpsCallable(functions, 'uploadInvoiceDocument');
    
    const result = await uploadFunction({
      pdfData: base64Data,
      invoiceId,
      invoiceNumber,
      docTypeKey,
      metadata: additionalMetadata
    });

    console.log('Invoice document upload completed successfully:', result.data);

    return result.data.metadata;
  } catch (error) {
    console.error('Error uploading invoice document:', error);
    
    if (error.code) {
      throw new Error(`Failed to upload document (${error.code}): ${error.message}`);
    }
    throw new Error(`Failed to upload document: ${error.message}`);
  }
};

/**
 * Get all invoice documents (attachments)
 * @param {string} invoiceId - Invoice document ID
 * @returns {Promise<Object>} Attachments object with all document types
 */
export const getInvoiceDocuments = async (invoiceId) => {
  try {
    const docRef = doc(db, 'invoices', invoiceId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.attachments || {};
    }

    return {};
  } catch (error) {
    console.error('Error getting invoice documents:', error);
    throw new Error(`Failed to get invoice documents: ${error.message}`);
  }
};

/**
 * Delete specific invoice document type
 * @param {string} invoiceId - Invoice document ID
 * @param {string} docTypeKey - Document type key to delete
 * @param {string} storagePath - Storage path of the file
 * @returns {Promise<boolean>} Success status
 */
export const deleteInvoiceDocument = async (invoiceId, docTypeKey, storagePath) => {
  try {
    console.log('Deleting invoice document:', { invoiceId, docTypeKey, storagePath });

    // Call Cloud Function to delete the file and update Firestore
    const functions = getFunctions();
    const deleteFunction = httpsCallable(functions, 'deleteInvoiceDocument');
    
    const result = await deleteFunction({
      invoiceId,
      docTypeKey,
      storagePath
    });

    console.log('Invoice document deletion completed successfully:', result.data);

    return true;
  } catch (error) {
    console.error('Error deleting invoice document:', error);
    
    if (error.code) {
      throw new Error(`Failed to delete document (${error.code}): ${error.message}`);
    }
    throw new Error(`Failed to delete document: ${error.message}`);
  }
};

/**
 * Check which document types are uploaded for an invoice
 * @param {string} invoiceId - Invoice document ID
 * @returns {Promise<Object>} Object with boolean flags for each document type
 */
export const getInvoiceDocumentStatus = async (invoiceId) => {
  try {
    const attachments = await getInvoiceDocuments(invoiceId);
    
    return {
      invoice: !!attachments.invoice,
      deliveryOrder: !!attachments.deliveryOrder,
      purchaseOrder: !!attachments.purchaseOrder,
      rentalTerms: !!attachments.rentalTerms
    };
  } catch (error) {
    console.error('Error getting invoice document status:', error);
    return {
      invoice: false,
      deliveryOrder: false,
      purchaseOrder: false,
      rentalTerms: false
    };
  }
};

/**
 * Get download URL for a specific invoice document type
 * @param {string} invoiceId - Invoice document ID
 * @param {string} docTypeKey - Document type key
 * @returns {Promise<string|null>} Download URL or null if not found
 */
export const getInvoiceDocumentURL = async (invoiceId, docTypeKey) => {
  try {
    const attachments = await getInvoiceDocuments(invoiceId);
    const document = attachments[docTypeKey];
    
    if (!document || !document.storagePath) {
      return null;
    }

    // Get download URL via Cloud Function
    return await downloadPDFFromStorage(document.storagePath);
  } catch (error) {
    console.error('Error getting invoice document URL:', error);
    return null;
  }
};
